import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { formatCurrencyAdaptive } from "../utils/numberFormat";

export const HOVER_PREVIEW_DELAY_MS = 250;
export const HOVER_PREVIEW_MAX_ROWS = 5;
const GAP = 8;
const EDGE = 8;

const canHover = () => (
  typeof window !== "undefined"
  && typeof window.matchMedia === "function"
  && window.matchMedia("(hover: hover) and (pointer: fine)").matches
);

const formatSignedPnl = (value) => {
  const numeric = Number(value) || 0;
  const sign = numeric > 0 ? "+" : numeric < 0 ? "-" : "";
  return `${sign}${formatCurrencyAdaptive(Math.abs(numeric))}`;
};

const pnlColor = (value) => {
  const numeric = Number(value) || 0;
  if (numeric > 0) return "var(--theme-success)";
  if (numeric < 0) return "var(--theme-loss)";
  return "var(--theme-text-muted)";
};

// Places the card beside the anchor (right, else left, else below/above) and keeps it on screen.
export function getPreviewPosition(anchor, size, viewport) {
  const { width, height } = size;
  let left;
  let top;
  if (anchor.right + GAP + width <= viewport.width - EDGE) {
    left = anchor.right + GAP;
    top = anchor.top;
  } else if (anchor.left - GAP - width >= EDGE) {
    left = anchor.left - GAP - width;
    top = anchor.top;
  } else {
    left = anchor.left + anchor.width / 2 - width / 2;
    top = anchor.bottom + GAP + height <= viewport.height - EDGE
      ? anchor.bottom + GAP
      : anchor.top - GAP - height;
  }
  left = Math.min(Math.max(EDGE, left), Math.max(EDGE, viewport.width - EDGE - width));
  top = Math.min(Math.max(EDGE, top), Math.max(EDGE, viewport.height - EDGE - height));
  return { left: Math.round(left), top: Math.round(top) };
}

export function DayPreviewCard({ dateLabel, trades, total }) {
  const shown = trades.slice(0, HOVER_PREVIEW_MAX_ROWS);
  const hidden = trades.length - shown.length;
  return (
    <>
      <div className="mb-1.5 text-[11px] font-medium" style={{ color: "var(--theme-text-muted)" }}>
        {dateLabel}
      </div>
      <ul className="space-y-1">
        {shown.map((trade, index) => (
          <li key={trade.key || index} className="flex items-baseline justify-between gap-4">
            <span className="min-w-0 truncate">{trade.label}</span>
            <span className="shrink-0 font-mono tabular-nums" style={{ color: pnlColor(trade.pnl) }}>
              {formatSignedPnl(trade.pnl)}
            </span>
          </li>
        ))}
      </ul>
      {hidden > 0 ? (
        <div className="mt-1 text-[11px]" style={{ color: "var(--theme-text-muted)" }}>
          +{hidden} more
        </div>
      ) : null}
      <div
        className="mt-1.5 flex items-baseline justify-between gap-4 border-t pt-1.5 font-semibold"
        style={{ borderColor: "var(--theme-border)" }}
      >
        <span>Total</span>
        <span className="font-mono tabular-nums" style={{ color: pnlColor(total) }}>
          {formatSignedPnl(total)}
        </span>
      </div>
    </>
  );
}

/**
 * Wraps a calendar day cell and shows a small read-only summary of that day's trades
 * after a short hover. Mouse pointers only; never intercepts clicks.
 */
const DayHoverPreview = ({ preview, children }) => {
  const wrapperRef = useRef(null);
  const cardRef = useRef(null);
  const timerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const hide = useCallback(() => {
    clearTimer();
    setOpen(false);
    setPosition(null);
  }, []);

  useEffect(() => () => clearTimer(), []);

  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    window.addEventListener("keydown", hide);
    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
      window.removeEventListener("keydown", hide);
    };
  }, [open, hide]);

  useLayoutEffect(() => {
    if (!open || !cardRef.current || !wrapperRef.current) return;
    const anchorEl = wrapperRef.current.firstElementChild || wrapperRef.current;
    const anchor = anchorEl.getBoundingClientRect();
    const card = cardRef.current.getBoundingClientRect();
    setPosition(getPreviewPosition(
      anchor,
      { width: card.width, height: card.height },
      { width: window.innerWidth, height: window.innerHeight },
    ));
  }, [open]);

  const hasPreview = Boolean(preview && preview.trades && preview.trades.length);

  const handlePointerEnter = (event) => {
    if (!hasPreview || event.pointerType !== "mouse" || !canHover()) return;
    clearTimer();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setOpen(true);
    }, HOVER_PREVIEW_DELAY_MS);
  };

  const portalTarget = typeof document !== "undefined"
    ? document.fullscreenElement || document.body
    : null;

  return (
    <div
      ref={wrapperRef}
      className="h-full w-full"
      onPointerEnter={handlePointerEnter}
      onPointerLeave={hide}
      onPointerDown={hide}
    >
      {children}
      {open && hasPreview && portalTarget
        ? createPortal(
          <div
            ref={cardRef}
            role="tooltip"
            data-testid="day-hover-preview"
            className="pointer-events-none fixed z-[70] w-56 max-w-[calc(100vw-16px)] rounded-lg border px-3 py-2 text-xs shadow-pop"
            style={{
              left: position ? position.left : 0,
              top: position ? position.top : 0,
              visibility: position ? "visible" : "hidden",
              backgroundColor: "var(--theme-surface-raised)",
              borderColor: "var(--theme-border)",
              color: "var(--theme-text)",
            }}
          >
            <DayPreviewCard dateLabel={preview.dateLabel} trades={preview.trades} total={preview.total} />
          </div>,
          portalTarget,
        )
        : null}
    </div>
  );
};

export default DayHoverPreview;
